/* =====================================================================
   PLAKTON · pk.js
   Comportamiento común de las secciones propias (pk-*):
   carriles con flechas y arrastre, hero automático, vídeos diferidos
   y preguntas frecuentes. Se inicializa una vez por sección y vuelve a
   inicializarse cuando se edita la sección en el editor de temas.
   ===================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Carril horizontal: flechas + arrastre con ratón ---------- */
  function initRail(wrap) {
    var rail = wrap.querySelector('[data-pk-rail]');
    if (!rail || rail.dataset.pkInit) return;
    rail.dataset.pkInit = '1';

    var prev = wrap.querySelector('[data-pk-prev]');
    var next = wrap.querySelector('[data-pk-next]');

    function step() { return Math.max(rail.clientWidth * 0.85, 200); }
    var bar = wrap.querySelector('[data-pk-progress]');
    function updateArrows() {
      if (bar) {
        var vis = rail.clientWidth / Math.max(rail.scrollWidth, 1);
        var span = Math.max(rail.scrollWidth - rail.clientWidth, 1);
        bar.style.setProperty('--pk-p', (vis * 100).toFixed(2) + '%');
        bar.style.setProperty('--pk-x', ((rail.scrollLeft / span) * (1 / Math.max(vis, 0.01) - 1) * 100).toFixed(2) + '%');
      }
      if (!prev || !next) return;
      var max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
    }
    if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: reducedMotion ? 'auto' : 'smooth' }); });
    if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: reducedMotion ? 'auto' : 'smooth' }); });
    rail.addEventListener('scroll', function () { window.requestAnimationFrame(updateArrows); }, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();

    if (!rail.hasAttribute('data-pk-drag')) return;
    var isDown = false, moved = false, startX = 0, startScroll = 0;
    rail.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      isDown = true; moved = false;
      startX = e.pageX; startScroll = rail.scrollLeft;
    });
    window.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (!moved && Math.abs(dx) > 6) { moved = true; rail.classList.add('is-dragging'); }
      if (moved) { e.preventDefault(); rail.scrollLeft = startScroll - dx; }
    });
    window.addEventListener('mouseup', function () {
      if (!isDown) return;
      isDown = false;
      if (moved) setTimeout(function () { rail.classList.remove('is-dragging'); }, 0);
    });
    rail.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }

  /* ---------- Hero con rotación automática ---------- */
  function initHero(root) {
    var track = root.querySelector('[data-pk-hero-track]');
    if (!track || track.dataset.pkInit) return;
    track.dataset.pkInit = '1';

    var slides = track.querySelectorAll('[data-pk-slide]');
    var dots = root.querySelectorAll('[data-pk-dot]');
    var prev = root.querySelector('[data-pk-prev]');
    var next = root.querySelector('[data-pk-next]');
    var count = slides.length;
    if (count < 2) return;

    var autoplay = root.getAttribute('data-autoplay') === 'true' && !reducedMotion;
    var index = 0;
    var userPaused = false;
    var pauseBtn = root.querySelector('[data-pk-pause]');
    var peeks = root.querySelectorAll('[data-pk-peek]');

    /* La barra de la diapositiva activa marca el tiempo: al terminar de llenarse, pasa a la siguiente. */
    function goTo(i, instant) {
      index = (i + count) % count;
      track.scrollTo({ left: index * track.clientWidth, behavior: instant || reducedMotion ? 'auto' : 'smooth' });
      setDots();
    }
    function setDots() {
      dots.forEach(function (d, n) {
        var on = n === index;
        d.classList.toggle('is-done', n < index);
        if (on && !d.classList.contains('is-active')) {
          var f = d.querySelector('[data-pk-fill]');
          d.classList.add('is-active');
          if (f) { f.style.animation = 'none'; void f.offsetWidth; f.style.animation = ''; }
        } else if (!on) d.classList.remove('is-active');
        d.setAttribute('aria-current', on ? 'true' : 'false');
      });
      setPeek(peeks[0], index - 1);
      setPeek(peeks[1], index + 1);
    }
    function setPeek(el, i) {
      if (!el) return;
      var s = slides[(i + count) % count];
      var img = s.querySelector('img');
      var src = img ? (img.currentSrc || img.getAttribute('src')) : '';
      var pi = el.querySelector('[data-pk-peek-img]');
      if (pi && src) pi.setAttribute('src', src);
      var label = s.querySelector('.pk-hero__eyebrow') || s.querySelector('.pk-hero__title');
      var t = el.querySelector('[data-pk-peek-txt]');
      if (t) t.textContent = label ? label.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    function syncFromScroll() {
      var i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      index = Math.max(0, Math.min(count - 1, i));
      setDots();
    }
    function start() { if (autoplay && !userPaused) root.classList.remove('is-hold'); }
    function stop() { root.classList.add('is-hold'); }
    function restart() {
      var d = dots[index], f = d && d.querySelector('[data-pk-fill]');
      if (f) { f.style.animation = 'none'; void f.offsetWidth; f.style.animation = ''; }
    }
    if (autoplay) root.classList.add('is-auto');
    dots.forEach(function (d) {
      var f = d.querySelector('[data-pk-fill]');
      if (f) f.addEventListener('animationend', function () { if (d.classList.contains('is-active')) goTo(index + 1); });
    });

    if (pauseBtn) {
      if (!autoplay) pauseBtn.hidden = true;
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        pauseBtn.setAttribute('aria-label', pauseBtn.getAttribute(userPaused ? 'data-label-play' : 'data-label-pause'));
        root.classList.toggle('is-paused', userPaused);
        if (userPaused) stop(); else start();
      });
    }

    dots.forEach(function (d, n) { d.addEventListener('click', function () { goTo(n); restart(); }); });
    if (prev) prev.addEventListener('click', function () { goTo(index - 1); restart(); });
    if (next) next.addEventListener('click', function () { goTo(index + 1); restart(); });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    track.addEventListener('touchstart', stop, { passive: true });
    track.addEventListener('touchend', function () { setTimeout(start, 1500); }, { passive: true });
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { goTo(index + 1); restart(); }
      else if (e.key === 'ArrowLeft') { goTo(index - 1); restart(); }
    });

    var t;
    track.addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(syncFromScroll, 80); }, { passive: true });
    window.addEventListener('resize', function () { goTo(index, true); });

    setDots();
    if (!autoplay) stop();
    root.addEventListener('pk:destroy', stop);
  }

  /* ---------- Vídeos: se cargan y reproducen solo cuando se ven ---------- */
  var videoObserver = null;
  function playVideo(v) {
    if (!v.src && v.dataset.src) { v.src = v.dataset.src; v.load(); }
    if (v.hasAttribute('data-pk-autoplay') && !reducedMotion) {
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
  }
  function initVideo(v) {
    if (v.dataset.pkInit) return;
    v.dataset.pkInit = '1';
    v.muted = true;
    v.addEventListener('pause', function () { if (!v.dataset.pkAutoPause && v.hasAttribute('controls')) v.removeAttribute('data-pk-autoplay'); delete v.dataset.pkAutoPause; });
    if (!('IntersectionObserver' in window)) { playVideo(v); return; }
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) playVideo(el);
          else if (!el.paused) { el.dataset.pkAutoPause = '1'; el.pause(); }
        });
      }, { rootMargin: '200px 0px' });
    }
    videoObserver.observe(v);
  }

  /* ---------- Preguntas frecuentes ---------- */
  function initFaq(root) {
    if (root.dataset.pkInit) return;
    root.dataset.pkInit = '1';
    var buttons = root.querySelectorAll('[data-pk-faq-q]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') === 'true';
        buttons.forEach(function (b) {
          if (b === btn && !open) return;
          b.setAttribute('aria-expanded', 'false');
          var p = document.getElementById(b.getAttribute('aria-controls'));
          if (p && !p.hidden) {
            p.style.maxHeight = null;
            setTimeout(function () { if (b.getAttribute('aria-expanded') === 'false') p.hidden = true; }, reducedMotion ? 0 : 450);
          }
        });
        if (!open && panel) {
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          window.requestAnimationFrame(function () { panel.style.maxHeight = panel.scrollHeight + 'px'; });
        }
      });
    });
  }


  /* Propiedades sobre el vídeo: avanzan solas mientras se ven; al pasar el ratón se fija la elegida */
  function initSpecs(box) {
    if (box.dataset.pkReady) return;
    box.dataset.pkReady = '1';
    var items = box.querySelectorAll('[data-pk-spec]');
    var segs = box.querySelectorAll('.pk-vc__seg');
    if (items.length < 2) return;
    var i = 0, timer = null, visible = false, hold = false, TIME = 4500;
    function show(n) {
      i = (n + items.length) % items.length;
      items.forEach(function (el, k) { el.classList.remove('is-active'); if (k === i) { void el.offsetWidth; el.classList.add('is-active'); } });
      segs.forEach(function (el, k) { el.classList.toggle('is-done', k < i); el.classList.remove('is-active'); if (k === i) { void el.offsetWidth; el.classList.add('is-active'); } });
    }
    function stop() { clearInterval(timer); timer = null; }
    function play() { stop(); if (reducedMotion || !visible || hold) return; timer = setInterval(function () { show(i + 1); }, TIME); }
    items.forEach(function (el, k) {
      el.addEventListener('mouseenter', function () { hold = true; box.classList.add('is-paused'); stop(); show(k); });
      el.addEventListener('focus', function () { hold = true; box.classList.add('is-paused'); stop(); show(k); });
    });
    box.addEventListener('mouseleave', function () { hold = false; box.classList.remove('is-paused'); show(i); play(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { visible = en[0].isIntersecting; if (visible) { show(i); play(); } else stop(); }, { threshold: 0.35 }).observe(box.parentNode);
    } else { visible = true; play(); }
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else play(); });
  }


  /* ---------- Vista rápida: pasar fotos (flechas, puntos, teclado) ---------- */
  (function bindQvGal() {
    if (window.pkQvGalBound) return;
    if (!window.jQuery) { window.addEventListener('load', bindQvGal, { once: true }); return; }
    window.pkQvGalBound = true;
    var $ = window.jQuery;
    function nav(el) { return $(el).closest('.productView-images-wrapper').find('.productView-nav.slick-initialized').first(); }
    $(document).on('click', '[data-pk-qv-prev]', function (e) { e.preventDefault(); var n = nav(this); if (n.length) n.slick('slickPrev'); });
    $(document).on('click', '[data-pk-qv-next]', function (e) { e.preventDefault(); var n = nav(this); if (n.length) n.slick('slickNext'); });
    $(document).on('click', '[data-pk-qv-go]', function (e) { e.preventDefault(); var n = nav(this); if (n.length) n.slick('slickGoTo', parseInt(this.getAttribute('data-pk-qv-go'), 10)); });
    $(document).on('afterChange', '.halo-quick-view-popup .productView-nav', function (e, slick, cur) {
      if (e.target !== this) return;
      $(this).closest('.productView-images-wrapper').find('[data-pk-qv-go]').each(function () {
        this.classList.toggle('is-active', parseInt(this.getAttribute('data-pk-qv-go'), 10) === cur);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var pop = document.querySelector('.halo-quick-view-popup');
      if (!pop || !pop.offsetParent || /INPUT|TEXTAREA|SELECT/.test((document.activeElement || {}).tagName || '')) return;
      var btn = pop.querySelector(e.key === 'ArrowLeft' ? '[data-pk-qv-prev]' : '[data-pk-qv-next]');
      if (btn) btn.click();
    });
  })();

  window.pkInitRails = function (scope) { (scope || document).querySelectorAll('[data-pk-rail-wrap]').forEach(initRail); };
  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-pk-rail-wrap]').forEach(initRail);
    root.querySelectorAll('[data-pk-hero]').forEach(initHero);
    root.querySelectorAll('video[data-pk-video]').forEach(initVideo);
    root.querySelectorAll('[data-pk-faq]').forEach(initFaq);
    root.querySelectorAll('[data-pk-specs]').forEach(initSpecs);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* Editor de temas: reinicializa la sección al editarla */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-pk-hero]').forEach(function (h) { h.dispatchEvent(new CustomEvent('pk:destroy')); });
  });
})();
